import React, { useId } from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';

import { rem } from '../../../utilities/style';
import { body, headers } from '../../../styles/type';
import { colors } from '../../../theme/colors';
import { fontWeights } from '../../../theme/type';
import { media } from '../../../theme/media';

import Link from '../../Link';
import Image from '../../Image';
import TextRenderer from '../../TextRenderer';
import { ReactComponent as IconArrowRight } from '../../../images/icons/arrow-right.svg';

const Card = styled.div`
  position: relative;
  padding: ${rem(20)} ${rem(16)};
  border-radius: ${rem(10)};
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.20);
  color: ${colors.white};
  aspect-ratio: 210/300;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;

  &::before {
    content: '';
    position: absolute;
    z-index: -1;
    inset: 0;
    width: 100%;
    height: 100%;
    background-image: linear-gradient(0deg, rgba(0,28,51,0.75) 0%, rgba(0,28,51,0.5) 50%, rgba(0,28,51,0) 100%);
    opacity: 1;
    transition: opacity 300ms ease;
  }

  &::after {
    content: '';
    position: absolute;
    z-index: -1;
    inset: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 113, 206, 0.90);
    opacity: 0;
    transition: opacity 300ms ease;
  }

  &:hover {
    &::before {
      opacity: 0;
    }
    &::after {
      opacity: 1;
    }
  }

  ${({ darkMode }) => darkMode && `
    &:hover {
      box-shadow: 0px 0px 160px 0px rgba(0, 114, 206, 0.30), 0px 0px 14px 0px rgba(0, 114, 206, 0.20), 0px 0px 1px 0px rgba(0, 113, 206, 0.80);
    }
  `}
`;

const ArrowStyled = styled(IconArrowRight)`
  transition: transform 300ms ease;
  ${Card}:hover & {
    transform: translateX(5px);
  }
`;

const StyledImage = styled(Image)`
  position: absolute;
  inset: 0;
  z-index: -2;
`;

const Headline = styled.h3`
  ${headers.h6}
  margin-bottom: ${rem(16)};
`;

const Expandable = styled.div`
  display: grid;
  grid-template-rows: 0fr;
  overflow: hidden;
  transition: grid-template-rows 300ms ease;;

  ${Card}:hover & {
    grid-template-rows: 1fr;
  }
`;

const ExpandableContent = styled.div`
  min-height: 0;
`;

const Content = styled.p`
  ${body.small}
  padding-bottom: ${rem(16)};
`;

const StyledLink = styled(Link)`
  display: flex;
  align-items: center;
  gap: ${rem(10)};
  color: ${colors.white};
  cursor: pointer;

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    z-index: 1;
  }
`;

const LinkText = styled.span`
  ${body.regular}
  text-decoration: underline;
  font-weight: ${fontWeights.semiBold};

  ${media('<=small')} {
    display: none;
  }
`;

const LinkCard = ({ headline, image, text, buttonText, buttonLink, darkMode }) => {
  const uniqueId = useId();

  return (
    <Card darkMode={darkMode}>
      {image && (
        <StyledImage image={image} alt="" />
      )}
      {headline && (
        <Headline id={`headline-${uniqueId}`}>
          <TextRenderer text={headline} />
        </Headline>
      )}
      <Expandable>
        <ExpandableContent>
          {text && (
            <Content>
              <TextRenderer text={text} />
            </Content>
          )}
        </ExpandableContent>
      </Expandable>
      {buttonText && (
        <StyledLink
          id={`link-${uniqueId}`}
          to={buttonLink}
          aria-labelledby={`link-${uniqueId} headline-${uniqueId}`}
        >
          <LinkText>
            <TextRenderer text={buttonText} />
          </LinkText>
          <ArrowStyled />
        </StyledLink>
      )}
    </Card>
  );
};

LinkCard.propTypes = {
  headline: PropTypes.string,
  text: PropTypes.string,
  buttonText: PropTypes.string,
  buttonLink: PropTypes.string,
  darkMode: PropTypes.bool,
};

export default LinkCard;
